import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { renameMailFolder } from "@/lib/api/imap";
import { imapFolderLeafName } from "@/lib/mail/folderNavSections";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import type { MailFolder } from "@/lib/types/mail";

type Props = {
  open: boolean;
  accountId: number;
  folder: MailFolder | null;
  onClose: () => void;
  onRenamed: (folder: MailFolder, oldName: string) => void;
};

export function MailRenameFolderDialog({
  open,
  accountId,
  folder,
  onClose,
  onRenamed,
}: Props) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !folder) return;
    setName(imapFolderLeafName(folder.name));
    setError(null);
  }, [folder, open]);

  const handleClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!folder || busy) return;
    const trimmed = name.trim();
    if (!trimmed) return;

    setBusy(true);
    setError(null);
    try {
      const res = await renameMailFolder({
        accountId,
        folder: folder.name,
        name: trimmed,
      });
      if (!res.ok) {
        setError(res.message);
        return;
      }
      onRenamed(res.data.folder, res.data.oldName);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Renommage impossible.");
    } finally {
      setBusy(false);
    }
  }, [accountId, busy, folder, name, onClose, onRenamed]);

  return (
    <MailModalOverlay open={open} onClose={handleClose} labelledBy="mail-rename-folder-title">
      <div className="px-5 py-5">
        <h2 id="mail-rename-folder-title" className="text-base font-semibold text-[var(--mail-text)]">
          Renommer le dossier
        </h2>
        <div className="mt-4 space-y-4">
          <p className="text-xs leading-relaxed text-[var(--mail-muted)]">
            Le nouveau nom est appliqué sur le serveur IMAP (visible partout : Aurélia, webmail, Thunderbird…).
          </p>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-[var(--mail-text)]">Nouveau nom</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex. Projets, Clients…"
              className="w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSubmit();
              }}
            />
          </label>

          {error ? (
            <p className="text-xs text-red-600" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="rounded-lg border border-[var(--mail-border-soft)] px-3 py-2 text-xs font-medium disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={busy || !name.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--mail-accent)] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
              Renommer
            </button>
          </div>
        </div>
      </div>
    </MailModalOverlay>
  );
}
